// ─────────────────────────────────────────────────────────────────────────────
// Bluetooth label printing, straight from the browser (Web Bluetooth / BLE).
//
// The counter PC prints pouch labels through the print bridge: the backend
// merges the store's PRN template and parks the bytes in pawn_print_jobs, and a
// service on that PC writes them to the SATO's Windows share. That is why a save
// made on a phone still printed — it queued a job the *counter* collected. The
// label came out in another room.
//
// A phone has no share and no bridge. Its only local route to a printer used to
// be Chrome → the Android print service, which needs the operator to tap through
// a print dialog and only works for printers that registered a print plugin —
// which almost no cheap Bluetooth thermal printer does. So this module takes the
// other route: it speaks to the printer itself over BLE GATT and pushes raw
// printer commands, the same thing the bridge does over a Windows share. No
// dialog, no plugin, no PC — which is what makes "save the payment and the label
// comes out" true on a phone or tablet as well as at the counter.
//
// Three facts about the platform shape everything here:
//
//   • Web Bluetooth is secure-context only. On http://192.168.x.x (the LAN dev
//     server) `navigator.bluetooth` does not exist at all, so support is
//     reported with the reason, never as a silent no-op — see btSupport().
//   • Only BLE is reachable. A printer that is Bluetooth Classic (SPP) only
//     cannot be driven from any browser; the pick dialog simply will not list
//     it. Most current label/receipt printers are dual-mode and do appear.
//   • Picking a device needs a user gesture, but *reconnecting* to one already
//     granted does not. So the operator pairs once from the printer dialog and
//     every save after that prints silently — the connection is held in this
//     module, not in a component, so it survives navigating between screens.
// ─────────────────────────────────────────────────────────────────────────────

const SETTINGS_KEY = "pawn.btPrinter";

// Printer command languages we can emit. Which one a printer speaks is a
// property of the hardware and cannot be probed over GATT — the operator picks
// it once (pickDevice() pre-selects from the advertised name) and confirms with
// a test print.
export const PRINTER_LANGUAGES = [
  { id: "escpos", label: "ESC/POS — most 58 mm / 80 mm Bluetooth printers" },
  { id: "tspl", label: "TSPL — TSC, Xprinter, Munbyn label printers" },
  { id: "cpcl", label: "CPCL — Zebra / Honeywell mobile printers" },
  { id: "zpl", label: "ZPL — Zebra desktop label printers" },
];

export const DEFAULT_BT_SETTINGS = {
  // Auto-print on a successful save. Off until the operator has paired a
  // printer and seen a test label come out of it.
  enabled: false,
  deviceId: "",
  deviceName: "",
  language: "escpos",
  // "auto" = derive from the label stock (mm × dots/mm). A number pins the
  // print width to the printer's head instead — see labelRaster.js.
  widthDots: "auto",
  dpi: 203,
  copies: 1,
  // ESC/POS only: blank lines fed after the label so it clears the tear bar.
  feedLines: 3,
  cut: false,
  rotate180: false,
  // BLE has no exposed MTU. 180 bytes per write is comfortable for current
  // printers; a printer that drops data prints half a label, and dropping to
  // 20 (the guaranteed minimum) is the fix.
  chunkBytes: 180,
  // Escape hatch for a printer whose GATT service UUID is not in the list
  // below: without it in optionalServices the service is invisible to the page,
  // even after pairing.
  service: "",
  gapMm: 2,
};

// ─── Settings ────────────────────────────────────────────────────────────────
// Per-device, not per-user: this describes the printer in the operator's hand,
// and the same login is used at a counter with a SATO and on a phone with a
// pocket printer.

export const loadBtSettings = () => {
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    return { ...DEFAULT_BT_SETTINGS, ...(raw ? JSON.parse(raw) : null) };
  } catch {
    return { ...DEFAULT_BT_SETTINGS };
  }
};

export const saveBtSettings = (patch) => {
  const next = { ...loadBtSettings(), ...patch };
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next));
  } catch {
    /* private browsing — the choice just does not stick */
  }
  emit({});
  return next;
};

// ─── Support ────────────────────────────────────────────────────────────────

/**
 * Whether this browser can talk to a Bluetooth printer, and if not, the one
 * thing the operator would have to change. The insecure-context case is called
 * out separately because it is by far the most common: the same build works
 * over https and is completely absent over a plain-http LAN address.
 */
export const btSupport = () => {
  if (typeof navigator === "undefined" || typeof window === "undefined")
    return { supported: false, reason: "" };
  if (!window.isSecureContext)
    return {
      supported: false,
      reason:
        "Bluetooth printing needs a secure page. Open the site over https " +
        "(a plain http:// LAN address cannot use Bluetooth at all).",
    };
  if (!navigator.bluetooth)
    return {
      supported: false,
      reason:
        "This browser has no Web Bluetooth. Use Chrome or Edge on Android, " +
        "Windows or macOS — Safari and iOS do not support it.",
    };
  return { supported: true, reason: "" };
};

/**
 * Whether there is a Bluetooth radio switched on right now.
 *
 * btSupport() answers "can this browser speak BLE at all"; this answers "is
 * there anything to speak to it with". The difference is the whole reason a
 * counter PC with a USB label printer was being shown Chrome's "turn on
 * Bluetooth" panel: requestDevice() does not fail when the adapter is off — it
 * opens the chooser and asks the operator to switch it on. So the only way to
 * not ask is to know beforehand, which is what this is for.
 *
 * It also keeps the fall-through quick: connecting to a paired printer with the
 * radio off does not fail fast, it waits out the 15 s timeout in openDevice().
 *
 * getAvailability() is missing on some builds. There, silence is not evidence
 * the radio is off, so we answer "available" and let the connect attempt report
 * whatever actually happens.
 */
export const btRadioAvailable = async () => {
  if (!btSupport().supported) return false;
  try {
    if (!navigator.bluetooth.getAvailability) return true;
    return await navigator.bluetooth.getAvailability();
  } catch {
    return true;
  }
};

// ─── Known printer GATT services ────────────────────────────────────────────
// A BLE printer exposes a vendor "transparent serial" service with one writable
// characteristic; everything written to it goes to the print head. The UUIDs are
// per-chipset rather than standardised, so the page has to name every one it
// might want up front: with acceptAllDevices the browser hides any service not
// listed in optionalServices, even on a device the operator just paired.
const KNOWN_SERVICES = [
  0x18f0, // common ESC/POS BLE modules (char 0x2af1)
  0xff00, // Goojprt / Xprinter (char 0xff02)
  0xffe0, // HM-10 style serial bridge (char 0xffe1)
  0xfee7, // Telink modules
  0xfff0,
  0x1101, // some dual-mode modules mirror the SPP UUID
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip/ISSC transparent UART
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2", // Zebra
  "0000ffb0-0000-1000-8000-00805f9b34fb",
  "0000ff80-0000-1000-8000-00805f9b34fb",
  "38eb4a80-c570-11e3-9507-0002a5d5c51b", // Sato / some Bixolon
  "0000fef5-0000-1000-8000-00805f9b34fb",
];

// Standard SIG services a printer also exposes. Never a print channel, but
// several are writable, and writing a label into the current-time service
// succeeds and prints nothing — the worst possible failure here, because it
// looks exactly like a working setup with an out-of-paper printer.
const NON_PRINT_SERVICES = [
  "00001800", // Generic Access
  "00001801", // Generic Attribute
  "0000180a", // Device Information
  "0000180f", // Battery
  "00001805", // Current Time
  "00001812", // HID
  "0000fe59", // Nordic DFU
  "00001530", // Legacy Nordic DFU
];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// An unparseable UUID makes requestDevice throw before the chooser opens, so a
// typo in the advanced field must not take the whole pairing flow down with it.
const serviceList = (extra) => {
  const clean = String(extra || "")
    .trim()
    .toLowerCase();
  return UUID_RE.test(clean) && !KNOWN_SERVICES.includes(clean)
    ? [...KNOWN_SERVICES, clean]
    : KNOWN_SERVICES;
};

// ─── Connection (module-level, one per tab) ──────────────────────────────────

let device = null;
let characteristic = null;
let inFlight = null; // de-dupes concurrent connect attempts

let state = { phase: "disconnected", deviceName: "", error: "" };
const listeners = new Set();

export const getBtState = () => ({
  ...state,
  connected: !!characteristic && !!device?.gatt?.connected,
});

const emit = (patch) => {
  state = { ...state, ...patch };
  const snapshot = getBtState();
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* a listener that throws must not stop the others */
    }
  });
};

/** Subscribe to connection changes; fires immediately with the current state. */
export const subscribeBtPrinter = (fn) => {
  listeners.add(fn);
  fn(getBtState());
  return () => listeners.delete(fn);
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Every step here can hang indefinitely — a printer that is switched off but
// still paired never answers, and an un-bounded await would freeze the save.
const withTimeout = (promise, ms, message) =>
  Promise.race([
    promise,
    sleep(ms).then(() => {
      throw new Error(message);
    }),
  ]);

// The one characteristic that carries print data: any writable one, preferring
// those on a service we recognise (a printer often also exposes a writable
// config/OTA characteristic, and writing a label to that does nothing).
const findWriteCharacteristic = async (server) => {
  const services = await server.getPrimaryServices();
  let fallback = null;
  for (const service of services) {
    let chars = [];
    try {
      chars = await service.getCharacteristics();
    } catch {
      continue; // a service that refuses enumeration is not the print path
    }
    if (NON_PRINT_SERVICES.some((p) => String(service.uuid).startsWith(p)))
      continue;
    for (const ch of chars) {
      if (!ch.properties?.write && !ch.properties?.writeWithoutResponse)
        continue;
      const known = KNOWN_SERVICES.some(
        (u) =>
          service.uuid ===
          (typeof u === "number"
            ? `0000${u.toString(16).padStart(4, "0")}-0000-1000-8000-00805f9b34fb`
            : u),
      );
      if (known) return ch;
      // A printer whose serial service is not in the list above still has to
      // work: any other writable characteristic is the best remaining guess.
      if (!fallback) fallback = ch;
    }
  }
  return fallback;
};

const onDisconnected = () => {
  characteristic = null;
  emit({ phase: "disconnected" });
};

const openDevice = async (dev) => {
  emit({ phase: "connecting", deviceName: dev.name || "", error: "" });
  dev.removeEventListener?.("gattserverdisconnected", onDisconnected);
  dev.addEventListener?.("gattserverdisconnected", onDisconnected);

  const server = await withTimeout(
    dev.gatt.connect(),
    15000,
    "The printer did not answer. Check that it is switched on and in range.",
  );
  const ch = await withTimeout(
    findWriteCharacteristic(server),
    15000,
    "Could not read the printer's Bluetooth services.",
  );
  if (!ch) {
    try {
      server.disconnect();
    } catch {
      /* already gone */
    }
    throw new Error(
      "This device has no writable Bluetooth print channel. If it is a " +
        "Bluetooth Classic (SPP) printer it cannot be driven from a browser.",
    );
  }

  device = dev;
  characteristic = ch;
  emit({ phase: "ready", deviceName: dev.name || "", error: "" });
  return ch;
};

/**
 * Open the browser's device chooser and pair a printer. Must be called from a
 * user gesture — the chooser is a permission prompt.
 *
 * Returns the settings that were persisted (device id/name, and a language
 * guessed from the advertised name so the common cases need no configuring).
 */
export const pickBtPrinter = async () => {
  const support = btSupport();
  if (!support.supported) throw new Error(support.reason);

  const cfg = loadBtSettings();
  // acceptAllDevices rather than a service filter: filtering hides every
  // printer whose serial-service UUID is not in our list, and an operator
  // staring at an empty chooser has no way to tell that from "printer off".
  const dev = await navigator.bluetooth.requestDevice({
    acceptAllDevices: true,
    optionalServices: serviceList(cfg.service),
  });

  await openDevice(dev);

  return saveBtSettings({
    deviceId: dev.id || "",
    deviceName: dev.name || "Bluetooth printer",
    // Re-pairing the same printer keeps whatever the operator settled on —
    // their manual choice beats a guess from the model name. A *different*
    // printer starts from the guess, because carrying the old printer's
    // language over is how a working setup silently starts printing nothing.
    language:
      cfg.deviceId && dev.id === cfg.deviceId
        ? cfg.language
        : guessLanguage(dev.name),
  });
};

// Vendor hints from the advertised name. Only ever used to pre-select the
// dropdown — the test print is what actually confirms it. Matched as
// model-number prefixes ("ZQ521", "ZD421") rather than whole words, which is
// how printers actually name themselves.
const guessLanguage = (name) => {
  const n = String(name || "").toUpperCase();
  // Zebra and Honeywell mobile units. The ZQ/ZD split matters: both are Zebra,
  // but the pocket ones are driven with CPCL and the desktop ones with ZPL.
  if (/\b(ZQ|IMZ|QLN)\s?\d/.test(n) || /HONEYWELL|INTERMEC|\bPB\d{2}/.test(n))
    return "cpcl";
  if (/\b(ZD|ZT|ZE|GK|GX|GC|ZP|ZM)\s?\d/.test(n) || /ZEBRA/.test(n))
    return "zpl";
  // TSC's firmware, and the rebadges that ship it — which is most of the cheap
  // BLE label printers. Beeprt/Detonger BM-series (BM30LR and friends) name
  // themselves nothing like TSC but run TSPL2 down to the font files, so they
  // are matched explicitly rather than left to the ESC/POS default.
  if (
    /\bTSC\b|TTP-|TE[23]\d\d|DA2\d\d|ALPHA-/.test(n) ||
    /\bBM\d{2}|BEEPRT|DETONGER|\bXP-4\d\d|IDPRT|GODEX/.test(n)
  )
    return "tspl";
  return "escpos";
};

/**
 * Reconnect to the printer already paired with this origin, without any
 * prompt. This is what makes auto-print on save work: the operator pairs once,
 * and every later connect — including after a page reload — is silent.
 *
 * navigator.bluetooth.getDevices() is what makes the "after a reload" half
 * possible; where it is missing the device object is gone with the page, and
 * the operator has to tap Connect once per session.
 *
 * @returns {Promise<boolean>} whether a print channel is open
 */
export const reconnectBtPrinter = async () => {
  if (!btSupport().supported) return false;
  if (characteristic && device?.gatt?.connected) return true;
  if (inFlight) return inFlight;

  const cfg = loadBtSettings();
  const attempt = (async () => {
    try {
      // Same tab, printer went out of range and came back: the device object
      // is still ours, so this is a plain reconnect.
      if (device) {
        await openDevice(device);
        return true;
      }
      if (!cfg.deviceId || !navigator.bluetooth.getDevices) return false;
      const granted = await navigator.bluetooth.getDevices();
      const match = granted.find((d) => d.id === cfg.deviceId);
      if (!match) return false;
      await openDevice(match);
      return true;
    } catch (err) {
      emit({ phase: "error", error: err?.message || "Could not connect" });
      return false;
    }
  })();

  // Cleared from outside rather than in a finally block inside: the paths that
  // return without awaiting (no printer paired yet, no getDevices support) run
  // to completion *before* the assignment below, so a finally would clear a
  // field that had not been set — leaving a resolved "false" cached here and
  // every later reconnect answering from it instead of trying.
  inFlight = attempt;
  attempt.then(
    () => {
      if (inFlight === attempt) inFlight = null;
    },
    () => {
      if (inFlight === attempt) inFlight = null;
    },
  );
  return attempt;
};

/** Drop the connection but keep the pairing, so Connect reopens it silently. */
export const disconnectBtPrinter = () => {
  try {
    device?.gatt?.disconnect();
  } catch {
    /* already gone */
  }
  characteristic = null;
  emit({ phase: "disconnected", error: "" });
};

/** Forget the printer entirely — the next print has to pick one again. */
export const forgetBtPrinter = async () => {
  disconnectBtPrinter();
  try {
    await device?.forget?.();
  } catch {
    /* not implemented everywhere; the saved id is cleared regardless */
  }
  device = null;
  emit({ phase: "disconnected", deviceName: "", error: "" });
  return saveBtSettings({ deviceId: "", deviceName: "" });
};

/**
 * Send raw printer bytes.
 *
 * Written in chunks because BLE has no exposed MTU: a single 10 KB write is
 * rejected or silently truncated by most printer modules. Where the
 * characteristic supports acknowledged writes we use them — the ACK is free
 * flow control — and only fall back to write-without-response, which needs a
 * hand-rolled gap, when that is all the module offers.
 *
 * @param {Uint8Array} bytes
 * @param {object}  [opts]
 * @param {boolean} [opts.allowPick] open the chooser if nothing is paired yet.
 *                  Only true when called straight from a click; a save cannot
 *                  raise a permission prompt.
 */
export const btPrint = async (bytes, { allowPick = false } = {}) => {
  if (!bytes?.length) throw new Error("Nothing to print");
  const support = btSupport();
  if (!support.supported) throw new Error(support.reason);

  let ch = characteristic && device?.gatt?.connected ? characteristic : null;
  if (!ch) {
    const ok = await reconnectBtPrinter();
    ch = ok ? characteristic : null;
  }
  if (!ch) {
    if (!allowPick)
      throw new Error(
        loadBtSettings().deviceId
          ? "Bluetooth printer not connected — open the printer settings and tap Connect."
          : "No Bluetooth printer paired on this device yet.",
      );
    await pickBtPrinter();
    ch = characteristic;
  }
  if (!ch) throw new Error("No Bluetooth print channel");

  const cfg = loadBtSettings();
  const size = Math.max(20, Math.min(512, Number(cfg.chunkBytes) || 180));
  const acked = !!ch.properties?.write;

  emit({ phase: "printing", error: "" });
  try {
    for (let i = 0; i < bytes.length; i += size) {
      const chunk = bytes.slice(i, i + size);
      if (acked) {
        await (ch.writeValueWithResponse
          ? ch.writeValueWithResponse(chunk)
          : ch.writeValue(chunk));
      } else {
        await (ch.writeValueWithoutResponse
          ? ch.writeValueWithoutResponse(chunk)
          : ch.writeValue(chunk));
        await sleep(12);
      }
      // The BLE link is faster than the print head. Pausing every few KB keeps
      // a full-label raster inside the printer's own buffer — overflowing it
      // prints the top of the label and drops the rest.
      if (((i / size) | 0) % 16 === 15) await sleep(20);
    }
  } finally {
    emit({ phase: device?.gatt?.connected ? "ready" : "disconnected" });
  }
};
